/**
 * Histórico textual de conversa para prompts de IA.
 */

import { supabase } from '../../../lib/supabase';
import { cleanPhone, generateJidVariants } from '../../../utils/phoneUtils';
import type { FetchMessageHistoryOptions } from './types';

export const EMPTY_CHAT_HISTORY = 'Nenhuma conversa identificada.';

export interface DialogMessageInput {
    sender?: string | null;
    content?: string | null;
}

const DEFAULT_LIMIT = 60;

/** Formata mensagens do chat para prompt de IA (Atendente / Cliente). */
export function formatMessagesAsDialog(
    messages: DialogMessageInput[] | null | undefined
): string {
    if (!messages?.length) return EMPTY_CHAT_HISTORY;

    return messages
        .map((m) => {
            const who = m.sender === 'me' ? 'Atendente' : 'Cliente';
            const text = (m.content ?? '').trim() || '[Mídia]';
            return `${who}: ${text}`;
        })
        .join('\n');
}

/** Histórico textual de uma conversa (por telefone/WhatsApp) para prompts de IA. */
export async function fetchMessageHistoryText(
    options: FetchMessageHistoryOptions
): Promise<string> {
    const phoneStr = cleanPhone(options.whatsapp || options.phone || '');
    if (!phoneStr) return EMPTY_CHAT_HISTORY;

    const searchJids = generateJidVariants(phoneStr);
    let convQuery = supabase
        .from('chat_conversations')
        .select('id')
        .in('remote_jid', searchJids)
        .order('last_message_time', { ascending: false })
        .limit(1);

    if (options.organizationId) {
        convQuery = convQuery.eq('organization_id', options.organizationId);
    }

    const { data: conv, error: convError } = await convQuery.maybeSingle();
    if (convError) {
        console.error('[aiChatContext] resolve conversation:', convError.message);
        return EMPTY_CHAT_HISTORY;
    }
    if (!conv?.id) return EMPTY_CHAT_HISTORY;

    const limit = options.limit ?? DEFAULT_LIMIT;
    let msgQuery = supabase
        .from('chat_messages')
        .select('content, sender, timestamp')
        .eq('conversation_id', conv.id)
        .order('timestamp', { ascending: false })
        .limit(limit);

    if (options.organizationId) {
        msgQuery = msgQuery.eq('organization_id', options.organizationId);
    }

    const { data: messages, error: msgError } = await msgQuery;
    if (msgError) {
        console.error('[aiChatContext] fetch messages:', msgError.message);
        return EMPTY_CHAT_HISTORY;
    }
    if (!messages?.length) return EMPTY_CHAT_HISTORY;

    const chronological = [...messages].reverse();
    return formatMessagesAsDialog(chronological);
}
